




























































import { Component, Prop, Watch } from "vue-property-decorator";
import VueEasymde from "vue-easymde";
import DOMPurify from "dompurify";
import { marked } from "marked";
import CheckAdminLogin from "@/utils/CheckAdminLogin";
import UgcsApi from "@/services/Ugcs";
import MaterialApi from "@/services/Materials";
import { NewMaterial } from "@/api/lab-base-api-client";
import "easymde/dist/easymde.min.css";
import "simplemde-theme-base/dist/simplemde-theme-base.min.css";
import "github-markdown-css/github-markdown-light.css";
import highlight from "highlight.js";
import "highlight.js/styles/vs2015.css";
import MaterialGroupModule from "@/store/materialGroup";
import logger from "../lib/log/logger";

@Component({
  components: {
    VueEasymde,
  },
})
export default class MMaterialRegisterSection extends CheckAdminLogin {
  // 編集対象のmaterialUuid
  private targetUuid = "";

  // ブラウザ上で認識したイメージデータ
  private uploadedImage: FileReader["result"] = "";

  // 画像の名前
  private imgName = "";

  // fileデータ
  private file: Blob | null = null;

  // local(PC)への保存先
  private filePath = "";

  @Prop() private body!: string;

  // inputの初期値
  private material: NewMaterial = { materialGroup: [], title: "", content: "", ugcUuid: "" };

  // 登録するcontentUuid
  private get isMaterialGroupUuid() {
    return MaterialGroupModule.materialGroupUuid;
  }

  private configs = {
    autofocus: true,
    status: false,
    spellChecker: false,
    uploadImage: true,
    imageUploadFunction: this.imageUploadFunction,
    initialValue: "",
    previewClass: ["editor-preview", "markdown-body"],
    previewRender: (plainText: string) => this.customMarkdownParser(plainText),
    renderingConfig: {
      singleLineBreaks: false,
      codeSyntaxHighlighting: true,
      sanitizerFunction: (renderedHTML: string): string => {
        return this.sanitizeHtml(renderedHTML);
      },
    },
    toolbar: [
      "bold",
      "italic",
      "strikethrough",
      "heading",
      "code",
      "quote",
      "unordered-list",
      "ordered-list",
      "link",
      "table",
      "image",
      "horizontal-rule",
      "preview",
      "fullscreen",
    ],
  };

  @Watch("content")
  private customMarkdownParser(plainText: string) {
    marked.setOptions({
      highlight: (code, lang) => {
        return highlight.highlightAuto(code, [lang]).value;
      },
    });
    const renderer = new marked.Renderer();
    renderer.image = src => {
      return `<div class="material-img">
        <img src="${src}">
      </div>`;
    };
    return marked(plainText, { renderer });
  }

  private sanitizeHtml(html: string): string {
    const sanitizedHtml: string = DOMPurify.sanitize(html, {
      ALLOWED_TAGS: [
        "b",
        "br",
        "li",
        "ol",
        "ul",
        "p",
        "a",
        "img",
        "strong",
        "em",
        "del",
        "h1",
        "h2",
        "h3",
        "h4",
        "h5",
        "h6",
        "pre",
        "code",
        "blockquote",
        "table",
        "thead",
        "tbody",
        "tr",
        "th",
        "td",
        "hr",
      ],
    });
    return sanitizedHtml;
  }

  // ページ読み込み時に実行
  private async mounted() {
    await this.fetchEdit();
    await this.getUuid();
  }

  // 教材編集だった場合、初期値をセット
  private async fetchEdit() {
    if (this.$route.name === "MaterialEdit" && String(this.$route.query.section)) {
      this.targetUuid = String(this.$route.query.section);
      const materialRes = await MaterialApi.instance.getMaterialByUuid(this.targetUuid);
      if (materialRes.data.data?.material?.uuid === undefined) {
        logger.frontError("failed get material");
        throw new Error("failed get material");
      }

      this.material.title = materialRes.data.data.material.title;
      this.material.content = materialRes.data.data.material.content;
      this.material.materialGroup = [{ uuid: `${this.$route.params.content}` }];
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private async imageUploadFunction(
    file: File,
    onSuccess: (imageUrl: string) => void,
    onError: (errorMessage: string) => void
  ) {
    // アップロード実行
    // 成功した場合backendから画像のurlをレスポンスとして取得
    // urlが正しく存在したらonSuccess実行
    try {
      const sendResData = await this.send(file);
      if (sendResData.url) {
        onSuccess(sendResData.url);
      }
    } catch (err) {
      if (err instanceof Error) {
        logger.frontError(`${err.name}: ${err.message}`);
        onError(err.message);
      }
    }
  }

  /**
   * apiサーバーに画像を送信、保存
   */
  private async send(file: File) {
    try {
      const ugcRes = await UgcsApi.instance.createUGC(file);
      if (ugcRes.data.data?.ugc?.uuid === undefined) {
        throw new Error("failed create ugc");
      }
      return ugcRes.data.data.ugc;
    } catch (err) {
      if (err instanceof Error) {
        logger.frontError(`${err.name}: ${err.message}`);
        console.log(err);
      }
      throw err;
    }
  }

  /**
   * inputのファイルの変更を検知してDOMで画像を扱えるようにする関数に渡す
   * @param e
   */
  private onFileChange(e: Event) {
    const files = (e.target as HTMLInputElement).files || (e as DragEvent).dataTransfer?.files;
    if (files && files.length > 0) {
      this.createImage(files[0]);
      this.imgName = files[0].name;
    }
  }

  /**
   * DOM上に画像を表示する処理
   * @param file
   */
  private createImage(file: Blob) {
    const reader = new FileReader();
    reader.onload = e => {
      if (e.target?.result) {
        this.uploadedImage = e.target?.result;
      }
    };
    reader.readAsDataURL(file);
    this.file = file;
  }

  /**
   * DOM上に表示している画像を削除
   */
  private remove() {
    this.uploadedImage = null;
  }

  // セクションの作成
  private async createMaterial() {
    const materialGroupUuid = this.isMaterialGroupUuid;
    this.material.materialGroup = [{ uuid: `${materialGroupUuid}` }];
    try {
      const materialRes = await MaterialApi.instance.createMaterial(this.material);
      if (materialRes.data.data?.material?.uuid === undefined) {
        throw new Error("failed create material");
      }
      return materialRes.data.data.material;
    } catch (err) {
      if (err instanceof Error) {
        logger.frontError(`${err.name}: ${err.message}`);
        console.log(err);
      }
      throw err;
    }
  }

  // セクションの編集
  private async editMaterial() {
    if (this.isMaterialGroupUuid) {
      const materialGroupUuid = this.isMaterialGroupUuid;
      this.material.materialGroup = [{ uuid: `${materialGroupUuid}` }];
    }
    try {
      const materialRes = await MaterialApi.instance.updateMaterialByUuid(this.targetUuid, this.material);
      if (materialRes.data.data?.material?.uuid === undefined) {
        throw new Error("failed create material");
      }
      return materialRes.data.data.material;
    } catch (err) {
      if (err instanceof Error) {
        logger.frontError(`${err.name}: ${err.message}`);
        console.log(err);
      }
      throw err;
    }
  }

  // get uuid
  private async getUuid() {
    const materialUuid = String(this.$route.query.section);
    if (this.$route.name === "MaterialEdit" && materialUuid) {
      this.targetUuid = materialUuid;
      const materialRes = await MaterialApi.instance.getMaterialByUuid(this.targetUuid);
      if (materialRes.data.data?.material?.uuid === undefined) {
        logger.frontError("failed get material");
        throw new Error("failed get material");
      }

      this.material.title = materialRes.data.data.material.title;
      this.material.content = materialRes.data.data.material.content;
      this.material.materialGroup = [{ uuid: `${this.$route.params.content}` }];
    }
  }
}
